<template>
  <div class="container" :class="{ bo_compact: compactMode }">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="mt-4" style="max-width:597px">
        <div v-if="step == 1">
            <div>
              <h3>Opprett rabatt</h3>

              <!-- RABATTEN: what it is called and how much it takes off -->
              <div class="bo_form-section-title">Rabatten</div>
              <div>
                <span class="bo_label">Rabattnavn</span>
                <input type='text' class="form-control" v-model="discount.name" />
              </div>

              <div>
                <span class="bo_label">Beskrivelse</span>
                <input type='text' class="form-control" v-model="discount.description" />
              </div>

              <div>
                <span class="bo_label">Prosent(%) rabatt</span>
                <input class="form-control" type="number" placeholder="f.eks 5" min="1" max="100" v-model="discount.percent" />
                <div v-if="percentError" class="bo_form-warning">{{ percentError }}</div>
              </div>

              <!-- NÅR: the hint lives with the fields it explains, not at the top of the page -->
              <div class="bo_form-section-title">Når gjelder den?</div>
              <SelectDate ref="selectDateFrom" label="Gyldig fra og med dato" :selectedDate="discount.fromDate"
                          :minDate="minFromDate" @update:selectedDate="discount.fromDate = $event" />
              <SelectDate ref="selectDateTo" label="Til og med dato" :selectedDate="discount.toDate"
                          :minDate="discount.fromDate" @update:selectedDate="discount.toDate = $event" />
              <p class="bo_form-hint">Skal rabatten gjelde kun én dag, velger du samme dato begge steder.</p>
              <div v-if="dateError" class="bo_form-warning">{{ dateError }}</div>

              <!-- HVOR: picked before the products, so each product's location badge can be read against it -->
              <div class="bo_form-section-title">Hvor gjelder den?</div>
              <div v-if="hasMultiLocations()">
                <span class="ingredient-title">Gjelder for følgende avdelinger</span>
                <fieldset>
                  <label v-for="(location, index) in locations" :key="index" class="multiple-checkbox-label">
                    <input type="checkbox" :name="location.name" :value="location.id" @click="setSelectedLocations" class="multiple-checkbox"
                           :checked="discount.validForLocationIds.indexOf(location.id) > -1" /> {{ location.name }}
                  </label>
                </fieldset>
              </div>

              <div>
                <span class="ingredient-title">Gjelder for følgende kanaler</span>
                <fieldset>
                  <label v-for="(channel, index) in channels" :key="index" class="multiple-checkbox-label">
                    <input type="checkbox" :name="channel.name" :value="channel.id" @click="setSelectedChannels" class="multiple-checkbox"
                           :checked="discount.validForChannelIds.indexOf(channel.id) > -1" /> {{ channel.name }}
                  </label>
                </fieldset>
              </div>

              <!-- HVA -->
              <div class="bo_form-section-title">Hva gjelder den for?</div>
              <div class="discount-select-product">
                <div class="d-flex clearfix">
                  <div class="bo_label left-align mt-2 ms-2">Gjelder for produkter</div>
                  <div class="right-align mt-2 mb-2 me-2">
                    <button v-if="selectedEntries.length == 0" type="button" class="bo_btn" @click="selectProduct"> Velg produkt </button>
                    <button v-else type="button" class="bo_select_remove_product_btn" @click="selectProduct"> Velg eller fjerne produkt </button>
                  </div>
                </div>

                <div v-for="entry in selectedEntries" :key="entry.item.id">
                  <div class="item">
                    <div class="item-col-left">
                    </div>
                    <div class="item-col-right">
                      <div class="flex-box">
                        <div>
                          <span>{{ entry.item.menuNr }} {{entry.item.name}} </span>
                          <!-- Only some variants: name them, so the list says what the discount is really for -->
                          <span v-if="entry.variantNames" class="bo_discount-variant-names">— {{ entry.variantNames.join(', ') }}</span>
                          <div v-if="locationNamesOf(entry.item).length > 0" class="bo_item-locations">
                            <span v-if="soldEverywhere(entry.item)" class="bo_item-location-badge">Alle avdelinger</span>
                            <template v-else>
                              <span v-for="name in locationNamesOf(entry.item)" :key="name" class="bo_item-location-badge">{{ name }}</span>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- The products the merchant chose that no selected avdeling actually sells: the discount
                   would simply never apply to them. Only computable now that both scopes are on one screen. -->
              <div v-if="itemsOutsideSelectedLocations.length > 0" class="bo_form-warning mt-3">
                {{ itemsOutsideSelectedLocations.length }} av {{ selectedEntries.length }} valgte produkter selges ikke hos
                avdelingene du har krysset av — rabatten vil ikke gjelde for dem:
                <span class="bo_form-warning-items">{{ itemsOutsideSelectedLocations.join(', ') }}</span>
              </div>

              <!-- A variant discount on a till or kiosk whose app is too old to know about variants. Said here,
                   before the discount starts, because on the device itself nothing looks wrong. -->
              <div v-if="outdatedDevicesForThisDiscount.length > 0" class="bo_form-warning mt-3">
                Enkelte enheter har en eldre app som ikke kjenner rabatt på varianter:
                <span class="bo_form-warning-items">{{ outdatedDeviceNames }}</span>.
                Der får variantene ikke rabatt, og andre rabatter som gjelder samtidig kan slutte å virke.
                Oppdater dem før rabatten starter.
              </div>

              <!-- VISNING: a presentation choice, decided last -->
              <div class="bo_form-section-title">Visning</div>
              <!-- .form-switch-wrapper is float:left, so anything after it rides up beside the toggle.
                   This container establishes a block formatting context and contains the float. -->
              <div class="bo_form-switch-row">
                <div class="form-switch-wrapper mt-2 mb-3">
                  <label class="form-switch">
                    <div class="form-switch-container">
                      <span class="me-2">Vis som en kategori</span>
                      <input type="checkbox" v-model="discount.showAsCategory" />
                      <i></i>
                    </div>
                  </label>
                </div>
              </div>

              <hr/>
              <!-- Tooltip on the wrapper: a disabled button gets pointer-events:none, so its own title
                   never shows. Says WHICH condition is unmet instead of leaving the merchant guessing. -->
              <div class="mb-5">
                <span :title="saveBlockedReason || 'Lagre rabatten'" style="display:inline-block">
                  <button type="button" class="bo_btn" @click="save" :disabled="!!saveBlockedReason"> Lagre </button>
                </span>
                <div v-if="saveBlockedReason" class="bo_form-hint">{{ saveBlockedReason }}</div>
                <div v-if="saveError" class="bo_form-warning">{{ saveError }}</div>
              </div>
            </div>
        </div>

        <div v-if="step == 2">
          <div>
            <div class="clearfix">
              <h3 class="float-start">Velg produkt</h3>
              <div class="float-end">
                  <button class="bo_btn" @click="back"> Tilbake </button>
                  <button v-if="selectedEntries.length > 0" class="bo_btn ms-3" @click="ok"> OK ({{ selectedEntries.length }})</button>
                  <button v-else class="bo_btn ms-3" @click="ok"> OK </button>
              </div>
            </div>

            <hr class="mb-0"/>

            <div class="mt-4 d-flex flex-wrap align-items-center gap-2">
              <button type="button" class="bo_btn" @click="selectAllOrRemoveAll"> {{selectAllOrRemoveAllButtonText()}} </button>
              <button type="button" class="bo_btn_secondary ms-auto"
                      :title="compactMode ? 'Vis bilder og beskrivelser igjen' : 'Kompakt visning: skjuler bilder og beskrivelser'"
                      @click="compactMode = !compactMode">
                <font-awesome-icon :icon="['fas', compactMode ? 'image' : 'list']"/>
                {{ compactMode ? 'Full visning' : 'Kompakt visning' }}
              </button>
            </div>

            <div class="mt-3">
              <div v-for="itemsOfCategory in itemsOfCategories" :key="itemsOfCategory.id" class="bo_items-of-category">
                <div class="bo_items_of_category_panel">
                  <span class="bo_items-of-category-header"> {{ itemsOfCategory.name }} </span>
                </div>

                <DiscountItemRow v-for="item in itemsOfCategory.items" :key="item.id" :item="item"
                                 :whole="isWhole(item)" :selectedVariantIds="selectedVariantIdsOf(item)"
                                 :locationNames="locationNamesOf(item)" :soldEverywhere="soldEverywhere(item)"
                                 @toggle-item="toggleItem" @toggle-variant="toggleVariant" />
              </div>
            </div>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import SelectDate from "@/components/other/SelectDate";
import MerchantUtil from "@/util/MerchantUtil";
import LoyaltyService from "@/services/loyalty/LoyaltyService";
import moment from "moment";
import LocationUtil from "@/util/LocationUtil";
import DiscountSelectionUtil from "@/util/DiscountSelectionUtil";
import DiscountItemRow from "@/components/loyalty/DiscountItemRow";

export default {
    components: {LoadingSpinner, SelectDate, DiscountItemRow},
    data() {
      return {
        loading: false,
        step: 1,

        channels: [
          {
            id: 'WEB_TAKEAWAY',
            name: 'Takeaway på Web(nettsiden)'
          },
          {
            id: 'WEB_DINE_IN',
            name: 'QR-bestilling ved bord'
          },
          {
            id: 'KIOSK',
            name: 'Selvbetjeningskiosk'
          },
          {
            id: 'POS',
            name: 'Kasse'
          }
        ],
        locations: [],
        compactMode: localStorage.getItem('bo_products_compact') === 'true', //Shared density preference with the products page
        //Whole products in itemIds, single variants in subselectionIds — see DiscountSelectionUtil
        selection: { itemIds: [], subselectionIds: [] },
        selectionBeforePicking: null, //Restored by Tilbake, so leaving the picker changes nothing
        items: [],
        itemsOfCategories: [],
        outdatedDevices: [], //Tills/kiosks whose app does not know variant discounts yet (from the server)
        saveError: '',

        discount: {
          merchant: '',
          type: 'discount',
          name: '',
          showAsCategory: false,
          description: '',
          percent: '',
          fromDate: (moment(new Date())).format('YYYY-MM-DD'),
          toDate: (moment(new Date())).format('YYYY-MM-DD'),
          itemIds: [],
          subselectionIds: [],
          validForLocationIds: [],
          validForChannelIds: [],
          active: true
        }
      };
    },
    watch: {
      compactMode(newVal){
        localStorage.setItem('bo_products_compact', newVal ? 'true' : 'false');
      },
      /**
       * Keep "til og med" at or after "fra og med".
       *
       * The end date starts at today, so moving the start into the future left it showing a date its own
       * picker no longer allows — a range that reads as valid but is not. Dragging it along is what the
       * merchant would do next anyway; they can still push it further out.
       */
      'discount.fromDate'(newFromDate){
        if(newFromDate && this.discount.toDate && this.discount.toDate < newFromDate){
          this.discount.toDate = newFromDate;
        }
      }
    },
    computed: {
      /**
       * What the discount covers, in menu order: each product once, with the names of its variants when it
       * is only some of them (variantNames is null for the whole product).
       */
      selectedEntries(){
        const menuOrder = [];
        this.itemsOfCategories.forEach(category => category.items.forEach(item => menuOrder.push(item)));
        this.items.forEach(item => {
          if(menuOrder.indexOf(item) === -1){
            menuOrder.push(item); //A product outside every listed category still counts
          }
        });
        return menuOrder.filter(item => DiscountSelectionUtil.isTouched(this.selection, item))
                        .map(item => ({
                          item: item,
                          variantNames: DiscountSelectionUtil.isWhole(this.selection, item) ? null
                              : DiscountSelectionUtil.variantsOf(item)
                                  .filter(variant => this.selection.subselectionIds.indexOf(variant.id) > -1)
                                  .map(variant => variant.name)
                        }));
      },
      /**
       * Tills and kiosks whose app predates variant discounts, when it matters: this discount has single
       * variants AND is valid on that kind of device. There the variant gets no discount — and because an
       * older app applies only ONE discount at a time, the highest, a discount of variants only can switch
       * the others off on that device.
       */
      outdatedDevicesForThisDiscount(){
        if(this.selection.subselectionIds.length === 0){
          return [];
        }
        const channels = Array.isArray(this.discount.validForChannelIds) ? this.discount.validForChannelIds : [];
        return this.outdatedDevices.filter(device => channels.indexOf(device.channel) > -1);
      },
      outdatedDeviceNames(){
        return this.outdatedDevicesForThisDiscount.map(device => device.name + ' (' + device.version + ')').join(', ');
      },
      /**
       * Why Lagre is disabled, or "" when it is not. The button needs a name, a percent and at least one
       * product; leaving the merchant to work out which of the three is missing is the actual complaint.
       */
      saveBlockedReason(){
        if(!this.discount.name || this.discount.name.trim().length === 0){
          return 'Gi rabatten et navn.';
        }
        if(!(this.discount.percent > 0)){
          return 'Sett en rabattprosent.';
        }
        if(this.percentError){
          return this.percentError;
        }
        if(this.dateError){
          return this.dateError;
        }
        if(this.selectedEntries.length === 0){
          return 'Velg minst ett produkt eller én variant.';
        }
        return '';
      },
      /** A new discount cannot start in the past — there is nothing it could have applied to. */
      minFromDate(){
        return (moment(new Date())).format('YYYY-MM-DD');
      },
      percentError(){
        const percent = Number(this.discount.percent);
        if(this.discount.percent === '' || this.discount.percent === null || isNaN(percent)){
          return '';
        }
        //A discount over 100% would pay the guest to order; under 1 is not a discount at all.
        if(percent > 100){
          return 'Rabatten kan ikke være over 100%.';
        }
        if(percent < 0){
          return 'Rabatten kan ikke være negativ.';
        }
        return '';
      },
      dateError(){
        const from = this.discount.fromDate;
        const to = this.discount.toDate;
        if(!from || !to){
          return '';
        }
        //Both are ISO-ish strings from SelectDate, so a plain comparison orders them correctly.
        return String(to) < String(from) ? 'Til-og-med-datoen er før fra-og-med-datoen.' : '';
      },
      /**
       * Names of chosen products that none of the ticked avdelinger sell — the discount can never apply
       * to them. Silent before, because the two scopes were never visible at the same time.
       */
      itemsOutsideSelectedLocations(){
        const selectedLocationIds = Array.isArray(this.discount.validForLocationIds)
            ? this.discount.validForLocationIds : [];
        if(this.locations.length < 2 || selectedLocationIds.length === 0){
          return [];
        }
        return this.selectedEntries.map(entry => entry.item).filter(item => {
          const ids = item.availableAtLocationIds || [];
          if(ids.length === 0){
            return false; //Sold everywhere
          }
          return !ids.some(id => selectedLocationIds.indexOf(id) > -1);
        }).map(item => item.name);
      },
    },
    mounted() {
      LoyaltyService.getDataForDiscountAdminGui().then(
          (response) => {
            this.locations = response.data.locations;
            for (let i = 0; i < this.locations.length; i++) {
              let locationId = this.locations[i].id;
              this.discount.validForLocationIds.push(locationId); //All available locations selected by default
            }

            let categories = response.data.categories;
            let items = response.data.items;
            this.items = items;
            this.itemsOfCategories = this.getItemsOfCategories(categories, items);
            this.outdatedDevices = response.data.outdatedDevices || [];
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      /**
       * Which of the merchant's locations sell this product — the same badges the products page shows.
       *
       * Useful here because a discount is itself limited to locations ("Gjelder for avdeling" below): a
       * product sold only at one avdeling gains nothing from a discount valid at another. Empty for a
       * single-location merchant, where every product is trivially everywhere.
       */
      locationNamesOf(item){
        if(this.locations.length < 2){
          return [];
        }
        const ids = item.availableAtLocationIds || [];
        if(ids.length === 0){
          return this.locations.map(location => location.name); //No restriction = sold everywhere
        }
        return this.locations.filter(location => ids.indexOf(location.id) > -1)
                             .map(location => location.name);
      },
      soldEverywhere(item){
        return this.locations.length > 1 && this.locationNamesOf(item).length === this.locations.length;
      },
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      selectAllOrRemoveAllButtonText(){
        return DiscountSelectionUtil.isAllSelected(this.selection, this.items) ? 'Fjern alle valgte' : 'Velg alle';
      },
      selectAllOrRemoveAll(){
        if(DiscountSelectionUtil.isAllSelected(this.selection, this.items)){
          DiscountSelectionUtil.clear(this.selection);
        }else {
          DiscountSelectionUtil.selectAll(this.selection, this.items);
        }
      },
      isWhole(item){
        return DiscountSelectionUtil.isWhole(this.selection, item);
      },
      selectedVariantIdsOf(item){
        return DiscountSelectionUtil.selectedVariantIdsOf(this.selection, item);
      },
      toggleItem(item){
        DiscountSelectionUtil.toggleItem(this.selection, item);
      },
      toggleVariant(item, variant){
        DiscountSelectionUtil.toggleVariant(this.selection, item, variant);
      },
      getItemsOfCategories(categories, items){
        var itemsOfCategories = [];
        for (var i = 0; i < categories.length; i++) {
          var category = categories[i];
          var tempItems = [];
          for (var x = 0; x < items.length; x++) {
            var item = items[x];
            if(item.categoryId == category.id){
              tempItems.push(item)
            }
          }
          //Remove already found items of category
          items = items.filter(function(item) {
            return tempItems.indexOf(item) === -1;
          });

          //Sort tempItems by asc sortOrder
          tempItems.sort(function(a, b) {
            if(a.sortOrder == b.sortOrder) { return 0; }
            if(a.sortOrder > b.sortOrder) {
              return 1;
            }else {
              return -1;
            }
          });

          var itemsOfCategory = {
            id  : category.id,
            sortOrder: category.sortOrder,
            name : category.name,
            items : tempItems
          };
          itemsOfCategories.push(itemsOfCategory);
        }

        //Sort itemsOfCategory by asc sortOrder
        itemsOfCategories.sort(function(a, b) {
          if(a.sortOrder == b.sortOrder) { return 0; }
          if(a.sortOrder > b.sortOrder) {
            return 1;
          }else {
            return -1;
          }
        });

        return itemsOfCategories;
      },
      setSelectedChannels(event){
        //console.log(JSON.stringify(this.discount.validForChannels));
        let channelId = event.target.value;
        if(event.target.checked){
          this.discount.validForChannelIds.push(channelId);
        } else {
          for (let i=0; i < this.discount.validForChannelIds.length; i++){
            if (this.discount.validForChannelIds[i] == channelId) {
              this.discount.validForChannelIds.splice(i,1);
              break;
            }
          }
        }
        //console.log(JSON.stringify(this.discount.validForChannels));
      },
      setSelectedLocations(event){
        //console.log(JSON.stringify(this.discount.validForLocationIds));
        let locationId = event.target.value;
        if(event.target.checked){
          this.discount.validForLocationIds.push(locationId);
        } else {
          for (let i=0; i < this.discount.validForLocationIds.length; i++){
            if (this.discount.validForLocationIds[i] == locationId) {
              this.discount.validForLocationIds.splice(i,1);
              break;
            }
          }
        }
        //console.log(JSON.stringify(this.discount.validForLocationIds));
      },
      selectProduct(){
        let fromDate =  this.$refs.selectDateFrom.getSelectedDate();
        let toDate =  this.$refs.selectDateTo.getSelectedDate();
        this.discount.fromDate = (moment(fromDate)).format('YYYY-MM-DD'); //bind it otherwise it will reset when go back
        this.discount.toDate = (moment(toDate)).format('YYYY-MM-DD');
        this.selectionBeforePicking = DiscountSelectionUtil.copy(this.selection);

        this.step = 2;
      },
      back(){
        //Tilbake leaves the choice as it was before the picker opened — it used to empty it, throwing away
        //products chosen on an earlier visit as well.
        if(this.selectionBeforePicking){
          this.selection = this.selectionBeforePicking;
        }
        this.step = 1;
      },
      ok(){
        this.step = 1;
      },
      save(){
        this.loading = true;
        this.saveError = '';

        let fromDate =  this.$refs.selectDateFrom.getSelectedDate();
        let toDate =  this.$refs.selectDateTo.getSelectedDate();

        this.discount.merchant = MerchantUtil.getMerchant();
        this.discount.fromDate = (moment(fromDate)).format('YYYY-MM-DD')
        this.discount.toDate = (moment(toDate)).format('YYYY-MM-DD')
        this.discount.itemIds = [...this.selection.itemIds];
        this.discount.subselectionIds = [...this.selection.subselectionIds];

        this.createDiscount(this.discount);
      },
      createDiscount(discount){
        LoyaltyService.createDiscount(discount).then(
            (response) => {
              let documentId = response.data;
              if(documentId){
                this.$router.push({ path: 'discounts' });
              } else {
                console.log("Creating discount failed");
                this.saveError = 'Rabatten ble ikke lagret. Prøv igjen.';
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.saveError = 'Rabatten ble ikke lagret. Prøv igjen.';
              this.loading = false; //The spinner used to stay up for good after a failed request
            }
        );
      }
    }
  };
</script>

<style scoped>
.bo_discount-variant-names{
  color: var(--bo-muted);
}
</style>
